import React, { useState } from 'react'
import AuthLayout from 'layouts/AuthLayout'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import 'styles/CardStyles.css' // Import shared styles
import AppBrandLogo from 'components/common/AppBrandLogo'
import LanguageSwitcher from 'components/common/LanguageSwitcher'

import { makeRequest } from 'services/request.service'
import { showAlert } from 'services/alerts.service'

const SignUpCard = () => {
  const { t } = useTranslation()
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [email, setEmail] = useState('')
  const [repeatEmail, setRepeatEmail] = useState('')
  const [password, setPassword] = useState('')
  const [repeatPassword, setRepeatPassword] = useState('')
  const [error, setError] = useState('')

  const [accessCode, setAccessCode] = useState('')
  const [unlocked, setUnlocked] = useState(false)
  const secret = 'letmein'
  const navigate = useNavigate()

  const validateForm = () => {
    if (!email) {
      setError(t('signup.errors.emailRequired'))
      return false
    }
    if (!/^\S+@\S+\.\S+$/.test(email)) {
      setError(t('signup.errors.emailInvalid'))
      return false
    }
    if (email !== repeatEmail) {
      setError(t('signup.errors.emailMismatch'))
      return false
    }
    if (!password) {
      setError(t('signup.errors.passwordRequired'))
      return false
    }
    if (password.length < 6) {
      setError(t('signup.errors.passwordTooShort'))
      return false
    }
    if (password !== repeatPassword) {
      setError(t('signup.errors.passwordMismatch'))
      return false
    }
    setError('')
    return true
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (validateForm()) {
      showAlert({
        text: t('signup.success'),
        icon: 'success'
      })
    }

    try {
      const result = await makeRequest({
        method: 'post',
        data: {
          firstName,
          lastName,
          email,
          password
        },
        url: '/signup',
        alertResult: true
      })
      if (result.status === 201) {
        navigate('/login')
      }
    } catch (error) {
      setError(error.message)
    }
  }

  if (!unlocked) {
    return (
      <div className='font-nunito min-h-screen flex items-center justify-center bg-greenPrimary'>
        <div className='absolute top-4 right-4'>
          <LanguageSwitcher variant='dark' />
        </div>
        <div className='bg-white p-8 rounded-xl shadow-md w-96 flex flex-col items-center'>
          <AppBrandLogo size='md' className='mb-2' />
          <h2 className='text-xl font-semibold text-greenPrimary mb-4'>{t('signup.inviteTitle')}</h2>
          <input
            type='password'
            value={accessCode}
            onChange={e => setAccessCode(e.target.value)}
            placeholder={t('signup.invitePlaceholder')}
            className='w-full px-4 py-2 mb-4 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500'
          />
          <button
            onClick={() => {
              if (accessCode === secret) {
                setUnlocked(true)
              } else {
                showAlert({
                  text: t('signup.invalidCode'),
                  icon: 'error'
                })
              }
            }}
            className='w-full bg-greenPrimary text-white py-2 rounded-md hover:bg-green-900 transition'
          >
            {t('signup.unlock')}
          </button>
          <div className='mt-6 text-center text-sm text-gray-600'>
            {t('signup.alreadyHaveAccount')}{' '}
            <a
              href='#login'
              onClick={e => {
                e.preventDefault()
                navigate('/login')
              }}
              className='text-greenPrimary font-semibold hover:underline'
            >
              {t('signup.backToLogin')}
            </a>
          </div>
        </div>
      </div>

    )
  }

  return (
    <AuthLayout>
      <h2 className='text-xl font-semibold text-greenPrimary mb-4'>{t('signup.createTitle')}</h2>

      <form onSubmit={handleSubmit} autoComplete='off' className='w-full'>
        {error && <p className='text-red-600 text-sm mb-4 text-center'>{error}</p>}

        <div className='mb-4'>
          <label className='block text-sm font-medium text-gray-600 mb-1'>{t('signup.firstName')}</label>
          <input
            type='text'
            name='firstName'
            value={firstName}
            onChange={e => setFirstName(e.target.value)}
            className='w-full px-3 py-2 border rounded-md bg-gray-100 focus:outline-none focus:ring-2 focus:ring-green-500'
          />
        </div>

        <div className='mb-4'>
          <label className='block text-sm font-medium text-gray-600 mb-1'>{t('signup.lastName')}</label>
          <input
            type='text'
            name='lastName'
            value={lastName}
            onChange={e => setLastName(e.target.value)}
            className='w-full px-3 py-2 border rounded-md bg-gray-100 focus:outline-none focus:ring-2 focus:ring-green-500'
          />
        </div>

        <div className='mb-4'>
          <label className='block text-sm font-medium text-gray-600 mb-1'>{t('signup.email')}</label>
          <input
            type='email'
            name='email'
            placeholder={t('signup.emailPlaceholder')}
            value={email}
            onChange={e => setEmail(e.target.value)}
            className='w-full px-3 py-2 border rounded-md bg-gray-100 focus:outline-none focus:ring-2 focus:ring-green-500'
          />
        </div>

        <div className='mb-4'>
          <label className='block text-sm font-medium text-gray-600 mb-1'>{t('signup.repeatEmail')}</label>
          <input
            type='email'
            name='repeatEmail'
            placeholder={t('signup.emailPlaceholder')}
            value={repeatEmail}
            onChange={e => setRepeatEmail(e.target.value)}
            className='w-full px-3 py-2 border rounded-md bg-gray-100 focus:outline-none focus:ring-2 focus:ring-green-500'
          />
        </div>

        <div className='mb-4'>
          <label className='block text-sm font-medium text-gray-600 mb-1'>{t('signup.password')}</label>
          <input
            type='password'
            name='password'
            placeholder={t('signup.passwordPlaceholder')}
            value={password}
            onChange={e => setPassword(e.target.value)}
            className='w-full px-3 py-2 border rounded-md bg-gray-100 focus:outline-none focus:ring-2 focus:ring-green-500'
          />
        </div>

        <div className='mb-6'>
          <label className='block text-sm font-medium text-gray-600 mb-1'>{t('signup.repeatPassword')}</label>
          <input
            type='password'
            name='repeatPassword'
            placeholder={t('signup.passwordPlaceholder')}
            value={repeatPassword}
            onChange={e => setRepeatPassword(e.target.value)}
            className='w-full px-3 py-2 border rounded-md bg-gray-100 focus:outline-none focus:ring-2 focus:ring-green-500'
          />
        </div>

        <button
          type='submit'
          className='w-full bg-greenPrimary text-white py-2 rounded-md hover:bg-green-900 transition'
        >
          {t('signup.submit')}
        </button>
      </form>

      <div className='mt-6 text-center text-sm text-gray-600'>
        {t('signup.alreadyHaveAccount')}{' '}
        <a
          href='#login'
          onClick={e => {
            e.preventDefault()
            navigate('/login')
          }}
          className='text-greenPrimary font-semibold hover:underline'
        >
          {t('signup.logIn')}
        </a>
      </div>
    </AuthLayout>
  )
}

export default SignUpCard
